
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import QuickActions from './QuickActions.vue';
import { DeviceInfo } from '@/types/device-info';
import { Mutation } from 'vuex-class';
import { mutations, actions } from '@/store/types';
import { SetDeviceMutationType } from '@/types/store/mutations';
import * as dlg from '@/modules/dlg';
import _get from 'lodash.get';
import api from '@/modules/api';
import { UserInfo } from '../../types/user-info';
import { Action, State } from 'vuex-class';
import { LoadDevicesActionType, LoadDeviceActionType } from '@/types/store/actions';
import { helpAndSupportURL } from '@/constants/constants';
import { DeviceType } from '@/types/device-info';
import { getErrorMessage } from '@/utils/common';

@Component({
    components: { QuickActions },
})
export default class DeviceQuickActions extends Vue {
    @Prop({ required: true })
    device!: DeviceInfo;

    actions = [
        { handler: this.help, key: 'help', title: 'Help and support' },
        { handler: this.locate, key: 'locate', title: 'Locate' },
        { handler: this.alarm, key: 'alarm', title: `Turn ${this.device.alarm === true ? 'off' : 'on'} alarm` },
        { handler: this.stolen, key: 'stolen', title: 'Report stolen' },
        { handler: this.gotoPaymentHistory, key: 'paymentHistory', title: 'Payment history' },
        { handler: this.remove, key: 'remove', class: 'red_text', title: 'Remove device' },
        { handler: this.rekey, key: 'rekey', class: 'red_text', title: 'Rekey AirBolt' },
    ];

    @Prop({
        default: () => [],
    })
    options!: string[];

    @State('user')
    user!: UserInfo;

    @Action(actions.LOAD_DEVICES)
    loadDevices!: LoadDevicesActionType;

    @Action(actions.LOAD_DEVICE)
    loadDevice!: LoadDeviceActionType;

    @Mutation(mutations.SET_CURRENT_DEVICE)
    setCurrentDevice!: SetDeviceMutationType;

    help() {
        window.open(helpAndSupportURL, '_blank')?.focus();
        // this.setCurrentDevice({ device: this.device });
        // this.$router.push({ name: 'support' });
    }

    locate() {
        dlg.notImplemented();
    }

    async alarm() {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            const response = await api.updateDeviceAlarm(
                this.device.deviceUUID,
                this.device.alarm === true ? 'off' : 'on',
            );
            await this.loadDevice({ deviceUUID: this.device.deviceUUID });
            Vue.toasted.success(response.message);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    stolen() {
        dlg.notImplemented();
    }

    gotoPaymentHistory() {
        this.setCurrentDevice({ device: this.device });
        this.$router.push({ name: 'user-settings-payments', params: { deviceId: this.device._id as string } });
    }

    async remove() {
        if (this.device.deviceType === DeviceType.AirboltGps && !(this.device?.subscription?.status === 'Inactive'))
            return dlg.alertInfo(
                `Device has an active subscription, please cancel the subscription to delete the device.`,
            );

        const confirmed = await dlg.confirm(
            'Are you sure you want to delete the device from your account? Deleting the device will remove it from account.',
            'Device removal',
        );

        if (!confirmed) {
            return;
        }
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            let deviceId = this.device._id;
            await api.removeDevice(deviceId as string);
            await this.loadDevices({ userId: this.user._id as string });
            this.$store.commit(mutations.SET_CURRENT_DEVICE, { device: null });
            this.$router.push({ name: 'device-overview' });
            Vue.toasted.success(`Removed successfully`);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async rekey() {
        const confirmed = await dlg.confirm(
            'Are you sure you want to rekey device? After that all previous connection becomes invalid.',
            'Device rekeying',
        );

        if (!confirmed) {
            return;
        }

        dlg.notImplemented();
    }

    get visibleActions() {
        return this.actions.filter((action) => this.options.includes(action.key));
    }

    @Watch('device', { immediate: true, deep: true })
    onDeviceChanged(device: DeviceInfo) {
        this.actions = [
            { handler: this.help, key: 'help', title: 'Help and support' },
            { handler: this.locate, key: 'locate', title: 'Locate' },
            { handler: this.alarm, key: 'alarm', title: `Turn ${device.alarm === true ? 'off' : 'on'} alarm` },
            { handler: this.stolen, key: 'stolen', title: 'Report stolen' },
            { handler: this.gotoPaymentHistory, key: 'paymentHistory', title: 'Payment history' },
            { handler: this.remove, key: 'remove', class: 'red_text', title: 'Remove device' },
            { handler: this.rekey, key: 'rekey', class: 'red_text', title: 'Rekey AirBolt' },
        ];
    }
}
